import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { SiteLayout } from "@/components/site/Layout";
import { Section, SectionEyebrow } from "@/components/site/Section";

export const Route = createFileRoute("/auth")({
  ssr: false,
  component: AuthPage,
  head: () => ({
    meta: [
      { title: "Admin Sign In - Kristan Cole Network" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
});

function AuthPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) navigate({ to: "/admin" });
    });
  }, [navigate]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setMsg(null);
    try {
      if (mode === "signup") {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: { emailRedirectTo: `${window.location.origin}/admin` },
        });
        if (error) throw error;
        setMsg("Account created. If email confirmation is required, check your inbox; otherwise sign in.");
        setMode("signin");
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        navigate({ to: "/admin" });
      }
    } catch (err: any) {
      setMsg(err.message ?? "Something went wrong");
    } finally {
      setLoading(false);
    }
  }

  return (
    <SiteLayout>
      <Section tone="dark" className="pt-36 md:pt-36 lg:pt-36">
        <div className="mx-auto max-w-md">
          <SectionEyebrow>Admin</SectionEyebrow>
          <h1 className="headline-lg mb-8">{mode === "signin" ? "Sign in" : "Create admin account"}</h1>
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-foreground/65">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 w-full bg-white/5 border border-white/30 focus:border-primary focus:outline-none px-4 py-3 text-foreground"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-foreground/65">Password</label>
              <input
                type="password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 w-full bg-white/5 border border-white/30 focus:border-primary focus:outline-none px-4 py-3 text-foreground"
              />
            </div>
            {msg ? <p className="text-sm text-primary">{msg}</p> : null}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary px-6 py-3 text-xs uppercase tracking-[0.18em] text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {loading ? "Working…" : mode === "signin" ? "Sign in" : "Create account"}
            </button>
            <button
              type="button"
              onClick={() => setMode(mode === "signin" ? "signup" : "signin")}
              className="w-full text-xs uppercase tracking-[0.18em] text-foreground/65 hover:text-foreground"
            >
              {mode === "signin" ? "Need an account? Sign up" : "Have an account? Sign in"}
            </button>
          </form>
          <p className="mt-6 text-xs text-foreground/55">
            The first account to claim admin gets it automatically. Subsequent users must be granted admin by an existing admin.
          </p>
        </div>
      </Section>
    </SiteLayout>
  );
}
